import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Button,
  TextField,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Modal,
  CircularProgress,
  IconButton,
  InputAdornment,
  useTheme
} from '@mui/material';
import { FaSearch, FaTrash } from 'react-icons/fa';
import { Pencil } from 'lucide-react';
import { useColorMode } from '../../../../../theme/ThemeContext';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import Table from '../../../admincomponents/Table';

const API_URL = 'https://tdtlworld.com/dss-kaas-backend/master/occupations/';

const OccupationManager = () => {
  const theme = useTheme();
  const { mode } = useColorMode();
  const [occupations, setOccupations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [occupationInput, setOccupationInput] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState(null);
  


  const token = localStorage.getItem('jwttoken');

  useEffect(() => {
    fetchOccupations();
  }, []);

  const fetchOccupations = async () => {
    setIsPageLoading(true);
    try {
      const res = await fetch(API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const error = await res.json();
        Swal.fire('Error', error.detail || 'Failed to fetch occupations', 'error');
        return;
      }
      const data = await res.json();
      setOccupations(data);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Network error', 'error');
    } finally {
      setIsPageLoading(false);
    }
  };

  const filteredOccupations = occupations.filter((o) =>
    o.name.toLowerCase().includes(searchTerm.toLowerCase())
  );



  const handleShowModal = (item = null) => {
    if (item) {
      setOccupationInput(item.name);
      setEditItem(item);
    } else {
      setOccupationInput('');
      setEditItem(null);
    }
    setShowModal(true);
  };

  const handleClose = () => {
    setOccupationInput('');
    setEditItem(null);
    setShowModal(false);
  };

  const columns = [
    { 
      key: 'srNo', 
      label: 'Sr No.', 
      render: (row, index) => (
        <Typography variant="body2" color="text.secondary">
          {index + 1}
        </Typography>
      )
    },
    { 
      key: 'name', 
      label: 'Occupation', 
      sortable: true,
      render: (row) => (
        <Typography variant="body2" color="text.primary" fontWeight={500}>
          {row.name}
        </Typography>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleShowModal(row)}
            sx={{ color: 'primary.main' }}
          >
            <Pencil size={18} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(row)}
            disabled={isDeletingId === row.id}
            sx={{ color: '#ff5370' }}
          >
            {isDeletingId === row.id ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <FaTrash size={16} />
            )}
          </IconButton>
        </Box>
      )
    }
  ];

  const handleAddOrUpdate = async () => {
    const trimmed = occupationInput.trim();
    if (!trimmed) {
      Swal.fire('Validation', 'Occupation name is required!', 'warning');
      return;
    }

    setIsLoading(true);
    const method = editItem ? 'PATCH' : 'POST';
    const url = editItem ? `${API_URL}${editItem.id}/` : API_URL;

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: trimmed }),
      });

      if (!res.ok) {
        const error = await res.json();
        Swal.fire({
          title: 'Error',
          text: error.detail || `Failed to ${editItem ? 'update' : 'add'} occupation`,
          icon: 'error',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        });
        return;
      }

      Swal.fire({
        title: editItem ? 'Updated!' : 'Added!',
        text: `Occupation ${editItem ? 'updated' : 'added'} successfully.`,
        icon: 'success',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        confirmButtonColor: theme.palette.primary.main,
      });
      fetchOccupations();
      handleClose();
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: 'Error',
        text: 'Network error',
        icon: 'error',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (occupation) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete "${occupation.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: theme.palette.text.secondary,
      confirmButtonText: 'Yes, delete it!',
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
    });

    if (confirm.isConfirmed) {
      setIsDeletingId(occupation.id);

      try {
        const res = await fetch(`${API_URL}${occupation.id}/`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          const error = await res.json();
          Swal.fire({
            title: 'Error',
            text: error.detail || 'Failed to delete',
            icon: 'error',
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
          });
          return;
        }

        Swal.fire({
          title: 'Deleted!',
          text: 'Occupation deleted successfully.',
          icon: 'success',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          confirmButtonColor: theme.palette.primary.main,
        });
        fetchOccupations();
      } catch (err) {
        console.error(err);
        Swal.fire({
          title: 'Error',
          text: 'Network error',
          icon: 'error',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        });
      } finally {
        setIsDeletingId(null);
      }
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 400 },
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: 24,
    p: 4,
    border: `1px solid ${theme.palette.divider}`
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: 'background.default' }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 800, color: 'text.primary', textAlign: 'center' }}>
        Manage Occupation
      </Typography>

      <Card sx={{ 
        p: 3, 
        mb: 4, 
        bgcolor: 'background.paper',
        color: 'text.primary'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search Occupation..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch color={theme.palette.text.secondary} />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 300 } }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={() => handleShowModal()}
          >
            + Add Occupation
          </Button>
        </Box>

        {isPageLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Table
            columns={columns}
            data={filteredOccupations}
            pagination={true}
            itemsPerPage={10}
          />
        )}
      </Card>

      <Modal
        open={showModal}
        onClose={handleClose}
      >
        <Box sx={modalStyle}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: 'text.primary' }}>
            {editItem ? 'Edit' : 'Add'} Occupation
          </Typography>
          
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Enter occupation name"
            value={occupationInput}
            onChange={(e) => setOccupationInput(e.target.value)}
            sx={{ mb: 3 }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button 
              variant="outlined" 
              onClick={handleClose} 
              disabled={isLoading}
              sx={{ color: 'text.primary', borderColor: 'divider' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddOrUpdate}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              {editItem ? 'Update' : 'Add'} Occupation
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default OccupationManager;
